/**
 * ImportResult (FE-4, PHASE1 §7.4). Success summary after a commit: how many
 * were imported (incl. selected possibles), skipped, rule-categorized, and
 * added to the inbox. Offers a fresh import and links onward to the inbox.
 */
import { CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '../../components/ui/Button.tsx';
import type { ImportCommitOut } from '../../lib/types.ts';

export interface ImportResultProps {
  result: ImportCommitOut;
  onNewImport: () => void;
}

export function ImportResult({ result, onNewImport }: ImportResultProps) {
  const stats: { label: string; value: number }[] = [
    { label: 'Importiert', value: result.imported },
    { label: 'Davon mögliche Duplikate', value: result.possible_imported },
    { label: 'Übersprungene Duplikate', value: result.skipped_duplicate },
    { label: 'Ausgeschlossen', value: result.skipped_excluded },
    { label: 'Per Regel kategorisiert', value: result.applied_rules },
    { label: 'Zur Kategorisierung', value: result.inbox_added },
  ];

  return (
    <div className="max-w-xl space-y-6">
      <div className="flex items-center gap-3">
        <CheckCircle2
          className="h-10 w-10 shrink-0 text-emerald-600 dark:text-emerald-400"
          aria-hidden
        />
        <div>
          <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Import abgeschlossen
          </p>
          <p className="text-sm text-slate-400 dark:text-slate-500">
            {result.imported} Umsätze wurden importiert.
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
        {stats.map((stat) => (
          <div key={stat.label}>
            <dt className="text-xs text-slate-400 dark:text-slate-500">{stat.label}</dt>
            <dd className="text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100">
              {stat.value}
            </dd>
          </div>
        ))}
      </dl>

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={onNewImport}>Neuer Import</Button>
        {result.inbox_added > 0 ? (
          <Link to="/inbox">
            <Button variant="secondary">Zum Posteingang</Button>
          </Link>
        ) : (
          <Link to="/transaktionen">
            <Button variant="secondary">Zu den Umsätzen</Button>
          </Link>
        )}
      </div>
    </div>
  );
}
